import {Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Ruleset, RULESET_TYPE_CONFIG} from '../models/rule-management.models';
import {PermissionService} from '../../../services/permission/permission.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faCopy,
  faEllipsisVertical,
  faFileExport,
  faLinkSlash,
  faLock,
  faPaperPlaneTop,
  faPlay,
  faScrewdriverWrench,
  faShareNodes,
  faShieldCheck,
  faTrash,
  faUserPen
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {CatalogSubmission, catalogSubmissionStatusLabel} from '../../../services/catalog.service';

@Component({
  selector: 'app-ruleset-card',
  standalone: true,
  imports: [
    FaIconComponent,
    BicButtonComponent,
    BicBadgeComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './ruleset-card.component.html',
})
export class RulesetCardComponent {
  @Input({required: true}) ruleset!: Ruleset;
  /**
   * Läuft für dieses Set gerade der Direktstart-Vorbereitungsschritt (Auto-myCheck upserten)?
   * Der Button ist dann deaktiviert, damit ein Doppelklick nicht zwei Läufe anstößt.
   */
  @Input() preparingRun = false;
  /**
   * Katalog: Darf aus dieser Stage eingereicht werden? Kommt einmalig von der Übersicht
   * ({@code GET /workflows/catalog/config}), damit nicht jede Karte selbst fragt.
   */
  @Input() catalogSubmissionAllowed = false;
  /** Letzte Katalog-Einreichung dieses Sets (nur für Admins geladen), sonst {@code null}. */
  @Input() catalogSubmission: CatalogSubmission | null = null;
  /** Direktstart: das Set ohne vorher gebauten Prüflauf ausführen. */
  @Output() execute = new EventEmitter<Ruleset>();
  @Output() open = new EventEmitter<Ruleset>();
  /** Prüflauf mit diesem myCheck als vordefinierter Regelauswahl anlegen. */
  @Output() createCheckRun = new EventEmitter<Ruleset>();
  @Output() duplicate = new EventEmitter<Ruleset>();
  @Output() delete = new EventEmitter<Ruleset>();
  /** Regelset als verschlüsseltes .bicrs-Paket herunterladen (für andere Projekte). */
  @Output() exportPackage = new EventEmitter<Ruleset>();
  /**
   * Adminfunktion: Regelwerk zentral bereitstellen. Absichtlich nicht „share"/„teilen" – das
   * schlichte Teilen ist für normale Nutzer geplant und hat damit nichts zu tun.
   */
  @Output() provideGlobally = new EventEmitter<Ruleset>();
  /** Mit anderen Projekten der Organisation teilen – nur im Eigentümerprojekt. */
  @Output() share = new EventEmitter<Ruleset>();
  /** Ein in dieses Projekt geteiltes Set wieder aus diesem Projekt entfernen. */
  @Output() unshareHere = new EventEmitter<Ruleset>();
  /** Ein anderes verlinktes Projekt zum Eigentümer machen – nur im Eigentümerprojekt. */
  @Output() transferOwnership = new EventEmitter<Ruleset>();

  @ViewChild('cardMenu') cardMenu!: BicDropdownComponent;

  protected readonly permissionService = inject(PermissionService);

  protected readonly faLock = faLock;
  protected readonly faShieldCheck = faShieldCheck;

  /**
   * Zentrale Bereitstellung ist ein plattform-globales Admin-Recht, kein Projektrecht.
   *
   * <p>Für Nicht-Admins ist der Eintrag gar nicht sichtbar (statt ausgegraut): das Privileg wird nur
   * per DB-Eintrag vergeben. Ein schon bereitgestelltes Regelwerk ({@code isPrebuilt}) lässt sich
   * nicht erneut bereitstellen.
   */
  protected get canProvideGlobally(): boolean {
    return !this.ruleset.isPrebuilt && !this.isLinkedIn
      && this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }

  /**
   * Menüeintrag „Zentral bereitstellen" = Katalog-Einreichung. Zusätzlich zum Admin-Recht muss die
   * Stage das Einreichen erlauben (nur beta, dev zum Testen) – sonst ist der Eintrag unsichtbar.
   */
  protected get canSubmitToCatalog(): boolean {
    return this.canProvideGlobally && this.catalogSubmissionAllowed;
  }

  /** Badge-Text zum letzten Stand: „Zentral bereitgestellt", aus der Einreichungszeit „Eingereicht"/„Abgelehnt". */
  protected get catalogBadgeLabel(): string | null {
    return this.catalogSubmission ? catalogSubmissionStatusLabel(this.catalogSubmission.status) : null;
  }

  protected get catalogBadgeTooltip(): string {
    const submission = this.catalogSubmission;
    if (!submission) return '';
    if (submission.status === 'REJECTED') {
      return submission.rejectReason
        ? `Im Admin Center abgelehnt: ${submission.rejectReason}`
        : 'Im Admin Center abgelehnt.';
    }
    if (submission.status === 'INSTALLED') {
      return 'In dieser Stage zentral bereitgestellt – nach Live übernimmt das Admin Center.';
    }
    return 'Im Admin Center zur Freigabe eingereicht.';
  }

  /**
   * Zentral bereitgestelltes („vorgefertigtes") Regelwerk – gehört keinem Projekt.
   *
   * <p>Hier gibt es <b>kein Öffnen, nur Duplizieren</b>, genau wie bei den zentral bereitgestellten
   * myChecks. Bewusst <i>unabhängig</i> von Adminrechten: die Kopie wird im Adminbereich verwaltet
   * ({@code admin/rulesets}), nicht in der Projektsicht. Würde ein Admin sie hier öffnen, bearbeitete
   * er unbemerkt aus einem Projektkontext heraus ein Regelwerk, das allen Empfängern gehört – und der
   * Draft→Publish-Ablauf des Adminbereichs wäre umgangen.
   */
  protected get isProvided(): boolean {
    return !!this.ruleset.isPrebuilt || (!!this.ruleset.readOnly && !this.isLinkedIn);
  }

  /**
   * Aus einem anderen Projekt der Organisation geteilt: ausführbar, duplizierbar und in Prüfläufen
   * auswählbar, bearbeitet wird es im Eigentümerprojekt. Anders als ein vorgefertigtes Set behält es
   * das Menü – dort steht „Aus diesem Projekt entfernen".
   */
  protected get isLinkedIn(): boolean {
    return !!this.ruleset.linkedIn;
  }

  /** Mit mindestens einem weiteren Projekt geteilt (aus Sicht egal welchen Projekts). */
  protected get isSharedWithProjects(): boolean {
    return (this.ruleset.sharedProjects?.length ?? 0) > 1;
  }

  /** „A (Eigentümer), B, C" – Eigentümer kommt vom Backend bereits zuerst. */
  protected get sharedProjectsLabel(): string {
    return (this.ruleset.sharedProjects ?? [])
      .map(p => p.owner ? `${p.name} (Eigentümer)` : p.name)
      .join(', ');
  }

  /**
   * Teilen geht nur mit eigenen Sets aus dem Eigentümerprojekt. Verlangt wird „myCheck verwalten"
   * – dasselbe Recht, das die Teilen-Regel bei Custom Roles prüft. Ob
   * die Rollen für ein Zielprojekt reichen, zeigt erst der Dialog.
   */
  protected get canShare(): boolean {
    return !this.isProvided && !this.isLinkedIn && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /** Aus diesem Projekt entfernen – dasselbe Recht wie Teilen, die Regel prüft das Backend. */
  protected get canUnshareHere(): boolean {
    return this.isLinkedIn && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /** Eigentümer ändern: nur ein geteiltes Set, nur im Eigentümerprojekt. */
  protected get canTransferOwnership(): boolean {
    return this.isSharedWithProjects && this.canShare;
  }

  protected readonly faUserPen = faUserPen;

  protected onTransferOwnership(): void {
    this.cardMenu?.hide();
    this.transferOwnership.emit(this.ruleset);
  }
  protected readonly faPaperPlaneTop = faPaperPlaneTop;
  protected readonly faShareNodes = faShareNodes;
  protected readonly faLinkSlash = faLinkSlash;

  protected onShare(): void {
    this.cardMenu?.hide();
    this.share.emit(this.ruleset);
  }

  protected onUnshareHere(): void {
    this.cardMenu?.hide();
    this.unshareHere.emit(this.ruleset);
  }
  protected readonly noExportPrebuiltTooltip = 'Vorgefertigte myChecks können nicht exportiert werden.';
  protected readonly noManageTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um diesen myCheck zu verwalten.';

  /** „myCheck verwalten" – bearbeiten, exportieren. Auf einer Kopie nicht möglich. */
  protected get canManage(): boolean {
    return !this.isProvided && !this.isLinkedIn
      && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /**
   * Duplizieren ist auch auf einem vorgefertigten Regelwerk erlaubt – es ist der vorgesehene Weg,
   * es zu verwenden. Gebraucht wird nur das Verwalten-Recht im Projekt, weil das Duplikat dort
   * entsteht. (Vorher hing Duplizieren an {@code canManage} und war damit auf genau den Sets
   * gesperrt, für die es gedacht ist.)
   */
  protected get canDuplicate(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /** „myCheck löschen". Nicht auf einer zentral bereitgestellten Kopie – die lebt im Adminbereich. */
  protected get canDelete(): boolean {
    return !this.isProvided && !this.isLinkedIn
      && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_DELETE');
  }

  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faScrewdriverWrench = faScrewdriverWrench;

  /** Eigene Berechtigung, nicht die der Prüfregeln. */
  protected get canCreateCheckRun(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  protected readonly noCreateCheckRunTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um Prüfläufe zu verwalten.';

  protected onCreateCheckRun(): void {
    this.cardMenu?.hide();
    this.createCheckRun.emit(this.ruleset);
  }

  protected onProvideGlobally(): void {
    this.cardMenu?.hide();
    this.provideGlobally.emit(this.ruleset);
  }

  protected readonly noDeleteTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um diesen myCheck zu löschen.';
  protected readonly lockTooltip = 'Dieser myCheck wurde von der BIC freigegeben und kann nicht bearbeitet werden. Er kann nur dupliziert werden.';
  protected readonly noExecuteTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um Prüfungen auszuführen.';
  protected readonly notExecutableTooltip = 'Dieser myCheck enthält noch keine ausführbaren Prüfregeln.';
  protected readonly faPlay = faPlay;

  /**
   * myCheck direkt ausführen.
   *
   * <p>Bewusst {@code PROJECT_CHECK_EXECUTE} (wie auf der myCheck-Karte) und NICHT das Verwalten-Recht:
   * Ausführen ist keine Verwaltungsaktion. Der dabei automatisch entstehende, unsichtbare myCheck ist
   * ein Implementierungsdetail – er darf kein zusätzliches Recht verlangen.
   *
   * <p>Gilt auch für zentral bereitgestellte (read-only) Sets: der Lauf findet im eigenen Projekt statt,
   * mit eigenen Dateien und Credits – genau wie bei zentral bereitgestellten myChecks. Nur Öffnen und
   * Bearbeiten bleiben dort gesperrt.
   */
  protected get canExecute(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE');
  }

  /**
   * Ein Set ohne Prüfregeln bzw. ohne aktiven Status kann nicht ausgeführt werden – der Lauf hätte
   * nichts zu prüfen (vgl. {@link isValid}, das dieselbe Bedingung als Karten-Icon zeigt).
   */
  protected get isExecutable(): boolean {
    return this.isValid;
  }
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCopy = faCopy;
  protected readonly faFileExport = faFileExport;

  /**
   * Export als .bicrs-Paket ist für vorgefertigte (BIC-bereitgestellte) myChecks gesperrt –
   * unabhängig von Admin-Rechten: die Pakete dienen der Wiederverwendung eigener Regelsets in
   * anderen Projekten, ein vorgefertigtes Set steht dort ohnehin bereits zur Verfügung.
   */
  protected get canExport(): boolean {
    return this.canManage && !this.ruleset.isPrebuilt;
  }
  protected readonly faTrash = faTrash;
  protected readonly faClock = faClock;

  /** Bevorzugt "zuletzt geändert"; fällt auf das Erstellungsdatum zurück, wenn keins bekannt ist. */
  protected get lastChangedAt(): string | Date | undefined {
    return this.ruleset.updatedAt ?? this.ruleset.createdAt;
  }
  protected readonly RULESET_TYPE_CONFIG = RULESET_TYPE_CONFIG;

  onOpen(): void {
    this.open.emit(this.ruleset);
  }

  protected onExecute(): void {
    this.execute.emit(this.ruleset);
  }

  protected get isValid(): boolean {
    return this.ruleset.status === 'ACTIVE' && this.ruleset.ruleCount > 0;
  }

  protected get validationTooltip(): string {
    return this.isValid ? 'Vollständig' : 'Anpassung notwendig';
  }

  onDelete(): void {
    this.cardMenu?.hide();
    this.delete.emit(this.ruleset);
  }

  onDuplicate(): void {
    this.cardMenu?.hide();
    this.duplicate.emit(this.ruleset);
  }

  onExport(): void {
    this.cardMenu?.hide();
    this.exportPackage.emit(this.ruleset);
  }
}
